import firebase from 'firebase/app';
import { Collection } from 'firestorter';

import CollectionName from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';
import { ANNOUNCEMENT_USER_TYPE_TAG_NAMES } from './Announcement';

const CommunityType = {
  POD: 'POD',
  CUSTOM: 'CUSTOM',
  TAG: 'TAG',
};

const GENERIC_COMMUNITY = Object.values(ANNOUNCEMENT_USER_TYPE_TAG_NAMES).map(({ label, value }) => ({
  id: value,
  name: label,
}));

const GENERIC_COMMUNITY_IDS = GENERIC_COMMUNITY.map((c) => c.id);
const isGenericCommunityId = (communityId) => GENERIC_COMMUNITY_IDS.includes(communityId);

/**
 * Class representing a coach Community (POD, CUSTOM, or TAG).
 * @class Community
 * @extends BaseDocument
 */
class Community extends BaseDocument {
  constructor(id, opts) {
    super(`${CollectionName.COMMUNITY}/${id}`, opts);
  }

  /** @returns {string} The community's display name. */
  get name() {
    return this.data.name || '';
  }

  /** @returns {string} The owning coach's user id. */
  get coach() {
    return this.data.coach || '';
  }

  /** @returns {string} The community type — one of `CommunityType` (POD | CUSTOM | TAG). */
  get type() {
    return this.data.type || '';
  }

  /**
   * The pod this community belongs to (POD communities only).
   * @returns {string | null}
   */
  get podId() {
    return this.data.podId || null;
  }

  /**
   * The client tags this community targets (TAG communities only).
   * @returns {string[]}
   */
  get customTags() {
    return this.data.customTags || [];
  }

  /** @returns {boolean} Whether the community is archived (read-only: existing posts stay visible). */
  get isArchived() {
    return !!this.data.isArchived;
  }

  /** @returns {firebase.firestore.Timestamp | undefined} When the community was created. */
  get createdAt() {
    return this.data.createdAt;
  }

  /** @returns {string | undefined} User id of the creator ('SYSTEM' for POD communities). */
  get createdBy() {
    return this.data.createdBy;
  }

  /** @returns {boolean} Whether this is a CUSTOM (name-only, product-linkable) community. */
  get isCustom() {
    return this.type === CommunityType.CUSTOM;
  }

  /** @returns {boolean} Whether this is a TAG (client-tag audience) community. */
  get isTagBased() {
    return this.type === CommunityType.TAG;
  }

  /** @returns {boolean} Whether this is a POD community. */
  get isPodBased() {
    return this.type === CommunityType.POD;
  }

  /**
   * Renames the community. Callers must ensure it is a CUSTOM community with no posts.
   * @param {string} name
   * @returns {Promise<void>}
   */
  async rename(name) {
    return this.updateFields({ name: name.trim(), updatedAt: new Date() });
  }

  /**
   * Archives the community. Callers must ensure it is a CUSTOM community with no members.
   * @param {string} userId
   * @returns {Promise<void>}
   */
  async archive(userId) {
    return this.updateFields({ isArchived: true, archivedAt: new Date(), archivedBy: userId });
  }

  /**
   * Whether this community has any (non-removed) posts referencing it.
   * Used to block renaming a custom community once it has posts.
   * @returns {Promise<boolean>}
   */
  async hasPosts() {
    const snapshot = await firebase
      .firestore()
      .collection(CollectionName.ANNOUNCEMENT)
      .where('communityId', '==', this.id)
      .where('isRemoved', '==', false)
      .limit(1)
      .get();
    return !snapshot.empty;
  }

  /**
   * Whether any product is linked to this community. A product-linked (CUSTOM) community must stay
   * product-based, so client tags can't be added to it (adding tags would make it a TAG community).
   * @returns {Promise<boolean>}
   */
  async hasLinkedProducts() {
    const snapshot = await firebase
      .firestore()
      .collection(CollectionName.PRODUCT)
      .where('communityId', '==', this.id)
      .limit(1)
      .get();
    return !snapshot.empty;
  }

  static async addDoc(data) {
    const communityCollection = new Collection(CollectionName.COMMUNITY);
    const { id } = await communityCollection.add(data);
    return id;
  }

  /**
   * Creates a coach community
   * @param {Object} params
   * @param {string} params.coachId
   * @param {string} params.name
   * @param {string} params.createdBy - Firebase user id of the creator.
   * @param {string[]} [params.customTags] - Optional custom client tags this community targets.
   * @returns {Promise<string>} The created community id.
   */
  static async createCommunity({
    coachId, name, createdBy, customTags = [],
  }) {
    return Community.addDoc({
      name: name.trim(),
      coach: coachId,
      createdBy,
      createdAt: new Date(),
      isArchived: false,
      // With tags → TAG (tag-holder audience); without → CUSTOM.
      type: customTags.length ? CommunityType.TAG : CommunityType.CUSTOM,
      ...(customTags.length ? { customTags } : {}),
    });
  }

  /**
   * Fetches a single community by id, whatever its coach or archived state. Used to resolve ids a
   * client still holds that the current coach's list no longer covers.
   * @param {string} id
   * @returns {Promise<Community | null>} Null when the document doesn't exist.
   */
  static async getById(id) {
    const communityDoc = new Community(id);
    await communityDoc.init();
    return communityDoc.exists ? communityDoc : null;
  }

  /**
   * Fetches a coach's communities (POD + CUSTOM).
   * @param {string} coachId
   * @param {Object} [options]
   * @param {boolean} [options.includeArchived=false]
   * @returns {Promise<Collection<Community>>}
   */
  static async getByCoach(coachId, { includeArchived = false } = {}) {
    const communityCollection = new Collection(CollectionName.COMMUNITY, {
      createDocument: ({ id }, opts) => new Community(id, { ...opts, disableObserverRefCount: true }),
      query: (ref) => {
        let query = ref.where('coach', '==', coachId);
        if (!includeArchived) {
          query = query.where('isArchived', '==', false);
        }
        return query;
      },
    });
    await communityCollection.fetch();
    return communityCollection;
  }
}

export default Community;
export {
  CommunityType,
  GENERIC_COMMUNITY,
  GENERIC_COMMUNITY_IDS,
  isGenericCommunityId,
};
